import { useState, type ReactNode } from 'react';
import { CircleCheck, CircleX } from 'lucide-react';
import type { ForgeUsageSheet, UsageSheetMethod, UsageSheetMethodStep } from '@shared/types/forge-usage-sheet';
import { isMethodProofCurrent, isUsageSheetMethodProven } from '@shared/forge-usage-sheet';
import { SheetRow, SheetSection } from './SheetLines';

const host = (url: string): string => url.replace(/^https?:\/\/(www\.)?/, '').slice(0, 70);

interface RouteConfig { kind?: string; urls?: string[]; queries?: string[]; domains?: string[] }

/** What a step reads, shown as is in the details: the proof of what each run will do. */
const stepTargets = (step: UsageSheetMethodStep): string => {
  if (step.action === 'web.collect_list') {
    const routes = Array.isArray(step.config.routes) ? step.config.routes as RouteConfig[] : [];
    return routes.map((route) => route.kind === 'pages'
      ? `Lit ${(route.urls ?? []).map(host).join(', ')}`
      : `Cherche ${(route.queries ?? []).map((query) => `« ${query} »`).join(', ')}${(route.domains ?? []).length > 0 ? ` sur ${(route.domains ?? []).join(', ')}` : ''}`).join(' ; puis ');
  }
  if (step.action === 'web.follow_each') return 'Ouvre la page de chaque élément';
  if (step.action === 'company.registry_each') return 'Registre officiel des entreprises';
  return step.action;
};

/** One quiet line: tested with which values and what it gave; what the test read stays in the details. */
const ProofLine = ({ sheet, method, showWhy }: { sheet: ForgeUsageSheet; method: UsageSheetMethod; showWhy: boolean }) => {
  const proof = method.proof;
  if (!proof || !isMethodProofCurrent({ ...sheet, method })) return <p className="text-sm text-domino-inkSoft">À tester à la prochaine réponse de Domino</p>;
  const values = Object.values(proof.values ?? {}).filter(Boolean).join(', ');
  const measure = proof.measure;
  const counts = measure ? ` · ${measure.inTarget} dans la cible sur ${measure.items}` : '';
  return isUsageSheetMethodProven(method)
    ? <p className="flex items-start gap-1.5 text-sm text-domino-inkSoft">
      <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" aria-hidden="true" />
      <span>Testée{values ? ` avec ${values}` : ' en direct'}{counts}{showWhy ? ` : ${proof.summary}` : ''}</span>
    </p>
    : <p className="flex items-start gap-1.5 text-sm text-domino-inkSoft">
      <CircleX className="mt-0.5 h-4 w-4 shrink-0 text-domino-prune" aria-hidden="true" />
      <span>Le test{values ? ` avec ${values}` : ''} a échoué : {proof.summary}</span>
    </p>;
};

/** The ways the Forge tried, folded: one line, opened on demand. */
const TrialsLine = ({ method }: { method: UsageSheetMethod }) => {
  const [open, setOpen] = useState(false);
  const trials = method.trials ?? [];
  if (trials.length < 2) return null;
  return <div className="text-sm">
    <button type="button" className="text-domino-inkSoft underline decoration-dotted underline-offset-4" aria-expanded={open} onClick={() => setOpen(!open)}>
      {trials.length} façons essayées
    </button>
    {open && <ul className="mt-1 space-y-1">
      {[...trials].reverse().map((trial) => <li key={`${trial.signature}:${trial.testedAt}`} className="flex items-start gap-1.5 text-domino-inkSoft">
        {trial.status === 'ok'
          ? <CircleCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-700" aria-hidden="true" />
          : <CircleX className="mt-0.5 h-3.5 w-3.5 shrink-0 text-domino-prune" aria-hidden="true" />}
        <span><span className="text-domino-ink">{trial.summary}</span> — {trial.verdict}</span>
      </li>)}
    </ul>}
  </div>;
};

/** Changes of the request the Forge measured when the source fell short: each with its figure. */
const AlternativesLines = ({ method, showWhy }: { method: UsageSheetMethod; showWhy: boolean }) => {
  const alternatives = method.alternatives ?? [];
  if (alternatives.length === 0) return null;
  return <div className="space-y-1 text-sm">
    <p className="text-domino-inkSoft">Autres demandes mesurées</p>
    {alternatives.map((alternative) => <SheetRow key={`${alternative.label}:${alternative.testedAt}`} showWhy={showWhy} why={alternative.change} removeLabel="">
      {alternative.label}
      <span className="text-domino-inkSoft"> · {alternative.status === 'ok'
        ? `${alternative.measure?.inTarget ?? 0} dans la cible sur ${alternative.measure?.items ?? 0}`
        : 'lecture en échec'}</span>
    </SheetRow>)}
  </div>;
};

/**
 * The method of the usage sheet (decisions Forge FDE, 02/10 and 22/10): found
 * freely while framing, then frozen as the tool's first steps. The user reads
 * each step in a few words and what it reads in the details, edits the
 * sentence, the step names and the judgement, and sees how it was tested.
 */
export const MethodSection = ({ sheet, method, showWhy, text, onChange }: {
  sheet: ForgeUsageSheet;
  method: UsageSheetMethod;
  showWhy: boolean;
  text: (value: string, label: string, onSave: (value: string) => void) => ReactNode;
  onChange: (method: UsageSheetMethod) => void;
}) => {
  const set = (patch: Partial<UsageSheetMethod>) => onChange({ ...method, ...patch });
  const setLabel = (index: number, label: string) =>
    set({ steps: method.steps.map((item, position) => (position === index ? { ...item, label } : item)) });
  return <SheetSection title="La méthode">
    <p className="text-sm text-domino-ink">{text(method.summary, 'La méthode', (summary) => set({ summary }))}</p>
    {method.steps.map((step, index) => <SheetRow key={`${index}:${step.action}`} showWhy={showWhy} why={stepTargets(step)} removeLabel="">
      <span className="text-domino-inkSoft">{index === 0 ? 'Source : ' : 'Puis : '}</span>
      {text(step.label, `Étape ${step.label}`, (label) => setLabel(index, label))}
    </SheetRow>)}
    {method.judgement && <p className="text-sm text-domino-ink">
      <span className="text-domino-inkSoft">Tri : </span>{text(method.judgement, 'Jugement', (judgement) => set({ judgement }))}
    </p>}
    {method.steps.length > 0 && <ProofLine sheet={sheet} method={method} showWhy={showWhy} />}
    <AlternativesLines method={method} showWhy={showWhy} />
    <TrialsLine method={method} />
  </SheetSection>;
};
